/**
 * Demo profiles — toggleable mock data for The Circle (peer-connect).
 *
 * Each profile represents a fictional member who has been active for
 * roughly 4 months. Posts use relative timestamps (ms offsets from
 * Date.now()) so they always feel current.
 *
 * Toggle via localStorage key SK_DEMO_PROFILES.
 */

import { SK_DEMO_PROFILES } from './constants/storageKeys';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const DAY = 86_400_000;
const HOUR = 3_600_000;

/** Build an ISO timestamp relative to now. */
function ago(days: number, hours = 0): string {
  return new Date(Date.now() - days * DAY - hours * HOUR).toISOString();
}

/* ------------------------------------------------------------------ */
/*  Profile definitions                                                */
/* ------------------------------------------------------------------ */

export interface DemoProfile {
  handle: string;
  avatarEmoji: string;
  birthYear: number;
  scdType: string;
  isMod: boolean;
  identities: string[];
}

export const DEMO_PROFILES: DemoProfile[] = [
  {
    handle: 'temi_j',
    avatarEmoji: '🌟',
    birthYear: 2006,
    scdType: 'HbSS',
    isMod: false,
    identities: ['Black', 'HbSS', 'she/her'],
  },
  {
    handle: 'dani.c',
    avatarEmoji: '🔥',
    birthYear: 2004,
    scdType: 'HbSC',
    isMod: true,
    identities: ['HbSC', 'he/him'],
  },
  {
    handle: 'lina_m',
    avatarEmoji: '🌙',
    birthYear: 2008,
    scdType: 'HbSβ-thal+',
    isMod: false,
    identities: ['South Asian', 'HbSβ-thal+', 'she/her'],
  },
  {
    handle: 'jay.w',
    avatarEmoji: '💜',
    birthYear: 2007,
    scdType: 'HbSS',
    isMod: false,
    identities: ['Black', 'HbSS', 'they/them'],
  },
  {
    handle: 'sofia.r',
    avatarEmoji: '✨',
    birthYear: 2003,
    scdType: 'HbSβ-thal0',
    isMod: false,
    identities: ['Latina', 'HbSβ-thal0', 'she/her'],
  },
];

/* ------------------------------------------------------------------ */
/*  Post definitions                                                   */
/* ------------------------------------------------------------------ */

export interface DemoPost {
  id: number;
  handle: string;
  avatarEmoji: string;
  birthYear: number;
  scdType: string;
  isMod: boolean;
  identities: string[];
  journey: string;
  type: string;
  text: string;
  createdAt: string;
  replyCount: number;
}

function profileOf(handle: string): DemoProfile {
  return DEMO_PROFILES.find((p) => p.handle === handle)!;
}

function post(
  id: number,
  handle: string,
  journey: string,
  type: string,
  text: string,
  createdAt: string,
  _replyCount = 0,
): DemoPost {
  const p = profileOf(handle);
  return {
    id,
    handle: p.handle,
    avatarEmoji: p.avatarEmoji,
    birthYear: p.birthYear,
    scdType: p.scdType,
    isMod: p.isMod,
    identities: p.identities,
    journey,
    type,
    text,
    createdAt,
    // Shown count matches the demo replies that exist.
    replyCount: getDemoReplyCount(id),
  };
}

/**
 * Returns all demo posts, newest first.
 * Timestamps are computed at call time so they stay relative to "now".
 */
export function getDemoPosts(): DemoPost[] {
  return [
    // ——— Recent (last few days) ———
    post(
      -1, 'temi_j', 'hospital', 'tip',
      'Started keeping a one-page crisis summary in my phone notes — name, diagnosis, usual meds, allergies, and what pain protocol works for me. Handed it to triage last visit and it actually sped things up.',
      ago(0, 6), 3,
    ),
    post(
      -2, 'jay.w', 'mental-health', 'solidarity',
      'Some weeks the fatigue is heavier than the pain. This was one of those weeks. Not looking for fixes — just saying it out loud.',
      ago(1, 4), 5,
    ),
    post(
      -3, 'dani.c', 'general', 'story',
      'Had a friend ask me "what does a crisis actually feel like?" for the first time in years of knowing each other. I told him honestly and he just listened. That meant more than any medical appointment this month.',
      ago(2, 12), 2,
    ),

    // ——— ~1 week ago ———
    post(
      -4, 'sofia.r', 'transition', 'question',
      'Moving from paediatric to adult haematology next year. Anyone else feel like they had to re-prove their whole history to a new team? How did you handle the handover?',
      ago(7, 3), 8,
    ),
    post(
      -5, 'lina_m', 'hospital', 'tip',
      'If your care plan says a specific pain protocol, ask the ER to look it up before they default to their own. I bring a printed copy now — less arguing at 3 a.m.',
      ago(9, 8), 4,
    ),

    // ——— ~2–3 weeks ago ———
    post(
      -6, 'temi_j', 'school', 'tip',
      'Asked my university disability office to email my lecturers at the start of term instead of me explaining every time. One less thing on my plate during flare season.',
      ago(16, 10), 6,
    ),
    post(
      -7, 'jay.w', 'mental-health', 'solidarity',
      'Grief sneaks up on me in weird places — like watching friends plan a spontaneous trip I know my body would veto. Not jealousy exactly, just a quiet ache. Anyone else?',
      ago(19, 2), 11,
    ),
    post(
      -8, 'dani.c', 'hospital', 'story',
      'Nurse last night said "I read your care plan before you got here." Six words and I almost cried. That is literally all we are asking for.',
      ago(21, 14), 9,
    ),

    // ——— ~1 month ago ———
    post(
      -9, 'sofia.r', 'mental-health', 'question',
      'Does anyone else struggle to tell the difference between SCD fatigue and depression fatigue? Some days I genuinely cannot figure out which one it is.',
      ago(32, 5), 14,
    ),
    post(
      -10, 'lina_m', 'general', 'solidarity',
      'My family still whispers about sickle cell like it is something to hide. Being South Asian with SCD already feels invisible enough — the silence at home makes it worse.',
      ago(38, 9), 7,
    ),

    // ——— ~2 months ago ———
    post(
      -11, 'temi_j', 'hospital', 'story',
      'Went to A&E and the doctor already knew what SCD was, asked about my usual protocol, and did not once question my pain score. I know that should be normal but it felt like a miracle.',
      ago(55, 11), 12,
    ),
    post(
      -12, 'jay.w', 'school', 'tip',
      'Got my school to let me record lectures so I can catch up after bad days. If your institution has a disability service, they can usually arrange this — you do not have to ask each teacher separately.',
      ago(60, 7), 4,
    ),
    post(
      -13, 'dani.c', 'transition', 'tip',
      'When you switch to adult care, ask your paediatric team for a written transition summary you can hand to the new team. Mine included med history, crisis frequency, and what has not worked. Saved weeks.',
      ago(65, 16), 6,
    ),

    // ——— ~3 months ago ———
    post(
      -14, 'sofia.r', 'hospital', 'story',
      'Spent eight hours in ER before anyone checked on me. When the haematologist finally arrived she apologized and fast-tracked everything. One good person can undo a lot of harm — but we should not have to rely on luck.',
      ago(82, 4), 10,
    ),
    post(
      -15, 'lina_m', 'mental-health', 'question',
      'How do you explain SCD to people without making it sound like you are asking for pity? I always over-explain or under-explain and I never know which is worse.',
      ago(88, 13), 9,
    ),

    // ——— ~4 months ago (early posts) ———
    post(
      -16, 'temi_j', 'general', 'solidarity',
      'Just joined and still figuring this out. Mostly here because nobody in my life really gets what it is like to plan your whole week around whether your body will cooperate.',
      ago(110, 2), 8,
    ),
    post(
      -17, 'jay.w', 'general', 'solidarity',
      'New here. Tired of feeling like the only person my age dealing with this. Hoping this space is different.',
      ago(112, 6), 13,
    ),
    post(
      -18, 'dani.c', 'general', 'story',
      'Been moderating online health communities for a couple of years. Happy to help keep this space safe. Drop me a message if anything feels off.',
      ago(115, 8), 3,
    ),
    post(
      -19, 'sofia.r', 'general', 'question',
      'First post. Anyone else here with beta-thal zero? Feels like most SCD resources focus on HbSS and I sometimes wonder if my experience counts.',
      ago(118, 5), 15,
    ),
    post(
      -20, 'lina_m', 'general', 'solidarity',
      'Hi everyone. I do not usually join online communities but my counsellor suggested it. Hoping to find people who understand without me having to explain from scratch every time.',
      ago(120, 10), 6,
    ),
  ];
}

/* ------------------------------------------------------------------ */
/*  Demo replies                                                       */
/* ------------------------------------------------------------------ */

export interface DemoReply {
  id: number;
  postId: number;
  text: string;
  createdAt: string;
  handle: string;
  avatarEmoji: string;
  isMod: boolean;
}

// [postId, handle, text, days ago, hours ago]
const REPLY_ROWS: [number, string, string, number, number][] = [
  [-1, 'lina_m', 'Doing this today. Did you put your haematologist contact on it too?', 0, 4],
  [-1, 'temi_j', 'Yes, name and clinic number at the bottom. Triage called them directly last time.', 0, 3],
  [-1, 'sofia.r', 'Saving this for my transition folder. Thank you.', 0, 1],
  [-2, 'dani.c', 'Hearing you. No fixes, just company.', 1, 3],
  [-2, 'lina_m', 'Same here this week. Fatigue days are real days.', 1, 2],
  [-2, 'sofia.r', 'Thank you for saying it out loud. It makes it easier for the rest of us to say it too.', 0, 20],
  [-3, 'jay.w', 'Being listened to is underrated. Glad he showed up for you.', 2, 8],
  [-3, 'temi_j', 'This made me want to tell my cousin more. Thanks for sharing.', 2, 2],
  [-4, 'temi_j', 'I asked my paeds nurse to write a one-page summary for the adult team. It helped a lot.', 6, 20],
  [-4, 'dani.c', 'Ask if you can meet the adult team once before the official switch. My clinic set up a joint visit.', 6, 12],
  [-4, 'lina_m', 'It is exhausting to retell everything. You are allowed to hand them the summary and ask them to read it first.', 5, 9],
  [-5, 'jay.w', 'The printed copy is such a good call. Phones die in the ER.', 9, 4],
  [-7, 'sofia.r', 'Every time. You named it well.', 16, 2],
  [-7, 'temi_j', 'Quiet ache is exactly it. Sending you care.', 15, 22],
  [-8, 'lina_m', 'Six words. This should be the standard everywhere.', 18, 10],
  [-8, 'jay.w', 'Screenshotting this for the next time someone says we ask for too much.', 18, 3],
  [-9, 'dani.c', 'Tracking both in check-ins helped me see patterns, and I took that to my care team. You are not alone in this.', 29, 5],
  [-9, 'lina_m', 'Same question for me. Talking to a counsellor who knows SCD helped me sort it out.', 28, 14],
  [-10, 'temi_j', 'You are not invisible here. South Asian SCD folks belong in this space.', 33, 6],
  [-10, 'sofia.r', 'Some of my family is the same. Finding people outside the family who get it helped.', 32, 18],
  [-16, 'dani.c', 'Welcome! Glad you found us.', 109, 20],
  [-17, 'lina_m', 'Welcome. You are definitely not the only one.', 111, 14],
  [-19, 'lina_m', 'Your experience counts. Beta-thal folks are part of this community.', 117, 9],
  [-20, 'jay.w', 'Welcome! No explaining from scratch needed here.', 119, 22],
];

export function getDemoReplies(postId: number): DemoReply[] {
  return REPLY_ROWS
    .filter(([pid]) => pid === postId)
    .map(([pid, handle, text, days, hours], i) => {
      const p = profileOf(handle);
      return {
        id: pid * 100 - i - 1,
        postId: pid,
        text,
        createdAt: ago(days, hours),
        handle: p.handle,
        avatarEmoji: p.avatarEmoji,
        isMod: p.isMod,
      };
    });
}

// Reaction counts shown on demo posts (🫂 support, ❤️ love, 🔥 strength).
export function getDemoReactions(): Record<number, Record<string, number>> {
  const out: Record<number, Record<string, number>> = {};
  for (let id = -1; id >= -20; id--) {
    const n = -id;
    out[id] = { '🫂': 6 + ((n * 7) % 19), '❤️': 3 + ((n * 5) % 13) };
    if (n % 3 !== 0) out[id]['🔥'] = 1 + ((n * 3) % 7);
  }
  return out;
}

export function getDemoReplyCount(postId: number): number {
  return REPLY_ROWS.filter(([pid]) => pid === postId).length;
}

/* ------------------------------------------------------------------ */
/*  Toggle                                                             */
/* ------------------------------------------------------------------ */

export function isDemoProfilesEnabled(): boolean {
  try {
    return localStorage.getItem(SK_DEMO_PROFILES) === 'on';
  } catch {
    return false;
  }
}

export function setDemoProfilesEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SK_DEMO_PROFILES, enabled ? 'on' : 'off');
  } catch {
    // localStorage unavailable
  }
}
